/**
 * What the sign-in screen should show. One decision, one answer.
 *
 * Extracted from the JSX because the JSX got it wrong twice, the same way both
 * times: two pieces of state describing one situation, each rendering its own
 * banner, and nothing in the code saying they were mutually exclusive.
 *
 * The first attempt chained the three CONFIG states and left the error banner
 * rendering above them independently. That still produced two banners for an
 * unreachable platform, and it shipped, and it was seen live:
 *
 *     Nothing answered                     the platform did not respond
 *     This platform has no admin surface   the platform answered
 *
 * A comment claiming the fix is not the fix. A function that returns exactly
 * one value is, because a second banner is then not something you can forget to
 * suppress - there is nowhere to put it.
 */

import type { AdminSignInConfig } from '../../lib/api.js';
import type { SignInFailure } from '../../lib/session.js';

/** Exactly one of these. The type is the guarantee. */
export type SignInView =
  | { kind: 'unreachable'; reason: SignInFailure }
  /** Reached it, and it serves no admin API. */
  | { kind: 'not-configured'; reason: SignInFailure | null }
  | { kind: 'dev'; reason: SignInFailure | null }
  | { kind: 'oidc'; reason: SignInFailure | null };

/**
 * `reason` travels with the view rather than being rendered beside it, so a
 * caller cannot show an error banner next to a contradictory one.
 *
 * `platform-unreachable` wins over everything. When the platform did not
 * answer, `config` is null because nothing could be ASKED — not because the
 * answer was "no admin API". Reading a null config as the latter is the
 * mistake that produced both banners.
 */
export function chooseSignInView(
  reason: SignInFailure | null,
  config: AdminSignInConfig | null,
): SignInView {
  if (reason === 'platform-unreachable') return { kind: 'unreachable', reason };
  if (config === null) return { kind: 'not-configured', reason };
  if (config.mode === 'dev') return { kind: 'dev', reason };
  return { kind: 'oidc', reason };
}
